import React, { ReactElement, useCallback, useEffect } from 'react';
import { Button, Form, Input, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Dispatch } from 'redux';
import IDeal, {
    IDealErrors,
} from '@/Api/Deal/IDeal';
import {
    dealAddAction,
    dealErrorSetAction,
    dealGetAction,
    dealValueSetAction,
} from '@/Lead/actions';
import {
    dealSelector,
    errorsSelector,
    savingSelector,
} from '@/Lead/selectors';
import { validateEmail as emailValidate } from '@/Lead/validators';
import IToken from '@/Auth/IToken';
import LeftDrawerLayout from './LeftDrawerLayout';
import FormItem from '@/UI/FormItem';
import { decodedTokenSelector } from '@/Auth/selectors';

export default function BasicInformation(): ReactElement {
    const navigate: NavigateFunction = useNavigate();
    const dispatch: Dispatch = useDispatch();
    const token: IToken = useSelector(decodedTokenSelector);
    const deal: IDeal = useSelector(dealSelector);
    const errors: IDealErrors = useSelector(errorsSelector);
    const saving: boolean = useSelector(savingSelector);

    useEffect(() => {
        if (token && token.dealUuid && !deal.uuid) {
            dispatch(dealGetAction());
        }
    }, [
        token,
        deal,
        dispatch,
    ]);

    const setError: (key: keyof IDealErrors, value: string) => void = useCallback((key: keyof IDealErrors, value: string) => {
        dispatch(dealErrorSetAction(key, value));
    }, [
        dispatch,
    ]);

    const setValue: (key: keyof IDeal, value: boolean|number|string) => void = useCallback((key: keyof IDeal, value: boolean|number|string) => {
        dispatch(dealValueSetAction(key, value));
    }, [
        dispatch,
    ]);

    const onClickContinue: () => void = (): void => {
        let valid: boolean = true;

        valid = validateEmail() && valid;
        valid = validateFirstName() && valid;
        valid = validateLastName() && valid;
        valid = validatePhone() && valid;

        if (!valid) {
            return;
        }

        if (!deal.uuid) {
            dispatch(dealAddAction('/get-your-quote/loan-amount'));
        } else {
            navigate('/get-your-quote/loan-amount');
        }
    };

    const onChangeEmail: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        setValue('email', event.target.value.substr(0, 100));
    }, [
        setValue,
    ]);

    const onChangeFirstName: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        setValue('firstName', event.target.value.substr(0, 50));
    }, [
        setValue,
    ]);

    const onChangeLastName: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        setValue('lastName', event.target.value.substr(0, 50));
    }, [
        setValue,
    ]);

    const onChangePhone: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        setValue('phone', event.target.value.substr(0, 20));
    }, [
        setValue,
    ]);

    const validateEmail: () => boolean = useCallback((): boolean => {
        let error: string;

        if (!deal.email || deal.email.length === 0) {
            error = 'Please enter your email address';
        } else if (!emailValidate(deal.email)) {
            error = 'Please enter a valid email address';
        }
        setError('email', error);

        return !error;
    }, [
        setError,
        deal,
    ]);

    const validateFirstName: () => boolean = useCallback((): boolean => {
        let error: string;

        if (!deal.firstName || deal.firstName.length === 0) {
            error = 'Please enter the first name';
        } else if (deal.firstName.length < 2) {
            error = 'Please enter at least 2 characters';
        }
        setError('firstName', error);

        return !error;
    }, [
        setError,
        deal,
    ]);

    const validateLastName: () => boolean = useCallback((): boolean => {
        let error: string;

        if (!deal.lastName || deal.lastName.length === 0) {
            error = 'Please enter the last name';
        } else if (deal.lastName.length < 2) {
            error = 'Please enter at least 2 characters';
        }
        setError('lastName', error);

        return !error;
    }, [
        setError,
        deal,
    ]);

    const validatePhone: () => boolean = useCallback((): boolean => {
        let error: string;

        if (!deal.phone || deal.phone.length === 0) {
            error = 'Please enter your phone number';
        }
        setError('phone', error);

        return !error;
    }, [
        setError,
        deal,
    ]);

    if (token && token.dealUuid && !deal.uuid) {
        return (
            <LeftDrawerLayout currentStep={1} pageName='basic-information'>
                <h3 className='headline'>Tell us more about you.</h3>
                <Spin indicator={<LoadingOutlined spin={true} />} />
            </LeftDrawerLayout>
        );
    }

    return (
        <LeftDrawerLayout currentStep={1} pageName='basic-information'>
            <h3 className='headline'>Tell us more about you</h3>
            <p className='intro'>
                We use these details to understand how we can help. There&apos;s no credit check at this stage.
            </p>
            <div className='broker-confirmation'>
                Are you a broker?<a className='link' href={`${process.env.PUBLIC_FRONTEND_HOST}/broker`}>Login</a>
            </div>
            <Form>
                <FormItem
                    className='first-name'
                    help={errors.firstName}
                    label='First name'
                    layout='vertical'
                    name='firstName'
                    validateStatus={errors.firstName ? 'error' : ''}
                >
                    <Input
                        placeholder='Enter first name'
                        type='text'
                        value={deal.firstName}
                        onBlur={validateFirstName}
                        onChange={onChangeFirstName}
                    />
                </FormItem>
                <FormItem
                    className='last-name'
                    help={errors.lastName}
                    label='Last name'
                    layout='vertical'
                    name='lastName'
                    validateStatus={errors.lastName ? 'error' : ''}
                >
                    <Input
                        placeholder='Enter last name'
                        type='text'
                        value={deal.lastName || ''}
                        onBlur={validateLastName}
                        onChange={onChangeLastName}
                    />
                </FormItem>
                <FormItem
                    className='email'
                    help={errors.email}
                    label='Email'
                    layout='vertical'
                    name='email'
                    validateStatus={errors.email ? 'error' : ''}
                >
                    <Input
                        placeholder='eg. name@example.com'
                        type='email'
                        value={deal.email || ''}
                        onBlur={validateEmail}
                        onChange={onChangeEmail}
                    />
                </FormItem>
                <FormItem
                    className='phone'
                    help={errors.phone}
                    label='Mobile/phone number'
                    layout='vertical'
                    name='phone'
                    validateStatus={errors.phone ? 'error' : ''}
                >
                    <Input
                        className='phone-number'
                        placeholder='0491 123 456'
                        type='text'
                        value={deal.phone || ''}
                        onBlur={validatePhone}
                        onChange={onChangePhone}
                    />
                </FormItem>
            </Form>
            <div className='actions'>
                <Button disabled={saving} loading={saving} type='primary' onClick={onClickContinue}>
                    Save and continue
                </Button>
            </div>
        </LeftDrawerLayout>
    );
}

